/* eslint-disable no-magic-numbers */
import { EventEmitter } from "events";
import httpStatus from "http-status";
import { sleep } from "@teaming/common-util";

/**
 * @code
 * const retryRequest = new RetryRequest( {
 * 	ioMethod: postToAPIMethod,
 * 	params: {
 * 		someData: "data"
 * 	},
 * 	successStatuses: [ httpStatus.NO_CONTENT, httpStatus.CREATED, ],
 * 	retryStatuses: [ httpStatus.SERVICE_UNAVAILABLE, ],
 * 	maxRetryAttempts: 4,
 * 	waitPeriodSeconds: 10,
 * } );
 *
 * return new Promise( async ( resolve, reject ) => {
 * 	let response;
 * 	let isSuccessful = false;
 * 	retryRequest.on( "success", ( { attempt, status, } ) => {
 * 		console.info( `successfully made request (attempt: ${ attempt }, last status: ${ status })` );
 * 		isSuccessful = true;
 * 	} );
 * 	retryRequest.on( "failure", ( { attempt, status, } ) => {
 * 		console.info( `failed to make request (attempt: ${ attempt }, last status: ${ status })` );
 * 	} );
 * 	retryRequest.on( "attempt", ( { attempt, status, retryInSeconds, } ) => {
 * 		console.info( `attempting to make request (attempt: ${ attempt }, last status: ${ status }, retry in: ${ retryInSeconds } seconds)` );
 * 	} );
 * 	// iteration halts on:
 * 	// - success (defined by success statuses)
 * 	// - failure (defined by non-retry statuses)
 * 	// - or when max retry attempts have been exceeded
 * 	for await ( const value of retryRequest ) {
 * 		response = value;
 * 	}
 * 	return isSuccessful ? resolve( response ) : reject( response );
 * } );
 */
export default class RetryRequest extends EventEmitter {
	constructor( {
		ioMethod,
		params,
		successStatuses = [ httpStatus.OK, ],
		retryStatuses = [ httpStatus.SERVICE_UNAVAILABLE, ],
		maxRetryAttempts = 5,
		waitPeriodSeconds = 5,
		allowResponseOverrides = true,
	} ) {
		super();
		this.ioMethod = ioMethod;
		this.params = params;
		this.successStatuses = successStatuses;
		this.retryStatuses = retryStatuses;
		this.maxRetryAttempts = maxRetryAttempts;
		this.waitPeriodSeconds = waitPeriodSeconds;
		this.allowResponseOverrides = allowResponseOverrides;
		this.isAborted = false;
		this._promise = null;
	}

	// @see https://javascript.info/async-iterators-generators
	[ Symbol.asyncIterator ]() {
		// eslint-disable-next-line consistent-this
		const retryRequest = this;
		return {
			current: 0,
			last: ( retryRequest.maxRetryAttempts + 1 ),
			status: 0,
			waitPeriodSeconds: retryRequest.waitPeriodSeconds,
			response: null,

			// eslint-disable-next-line max-statements
			async next() {
				const { current, last, status, response, waitPeriodSeconds, } = this;

				if ( retryRequest.successStatuses.includes( status ) ) {
					retryRequest.emit( "success", {
						attempt: current,
						status,
						response,
					} );

					return { done: true, };
				}

				if ( status !== 0 && !retryRequest.retryStatuses.includes( status ) ) {
					retryRequest.emit( "failure", {
						attempt: current,
						status,
						response,
					} );

					return { done: true, };
				}

				if ( current === last ) {
					retryRequest.emit( "exhaust", {
						attempt: current,
						status,
						response,
					} );

					return { done: true, };
				}

				if ( retryRequest.isAborted ) {
					retryRequest.emit( "abort", {
						attempt: current,
						status,
						response,
					} );

					return { done: true, };
				}

				if ( this.current >= 1 ) {
					retryRequest.emit( "queue", {
						attempt: current,
						status,
						response,
						waitPeriodSeconds,
					} );

					await sleep( waitPeriodSeconds * 1000 );

					if ( retryRequest.isAborted ) {
						retryRequest.emit( "abort", {
							attempt: current,
							status,
							response,
						} );

						return { done: true, };
					}
				}

				this.current += 1;

				retryRequest.emit( "attempt", {
					attempt: current,
					status,
					waitPeriodSeconds,
				} );

				const _params = {
					...retryRequest.params,
					attempt: current,
				};

				try {
					this.response = await retryRequest.ioMethod( _params );
				} catch ( e ) {
					// eslint-disable-next-line no-console
					console.error( `error response in attempt ${ current }`, e );
					this.response = e.response;
				}

				const { status: nextStatus, headers, } = this.response;
				this.status = nextStatus;

				let retryAfter = waitPeriodSeconds;
				if ( retryRequest.allowResponseOverrides ) {
					retryAfter = headers[ "retry-after" ] ||
						waitPeriodSeconds;
				}
				this.waitPeriodSeconds = parseInt( retryAfter, 10 );

				return { done: false, value: this.response, };
			},
		};
	}

	async abort( { clearEventHandlers = true, } = {} ) {
		this.isAborted = true;
		this.emit( "aborting" );
		if ( clearEventHandlers ) {
			this.removeAllListeners();
		}
		return this._promise || Promise.resolve();
	}

	async promise() {
		this._promise = this._promise || new Promise( async ( resolve, reject ) => {
			let response;
			let isSuccessful = false;
			this.on( "success", () => {
				isSuccessful = true;
			} );
			// iteration halts on:
			// - success (defined by success statuses)
			// - failure (defined by non-retry statuses)
			// - or when max retry attempts have been exceeded
			for await ( const value of this ) {
				response = value;
			}
			return isSuccessful ? resolve( response ) : reject( response );
		} );
		return this._promise;
	}
}
